import React from 'react';
import { AppButton, Box, Inscription, Preloader, RawButton, Text } from '@common-fe/common-fe';
import dayjs from 'dayjs';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import { DEFAULT_DATE_FORMAT, EMPTY_FIELD_VALUE, Roles } from '@/common/constants';
import { StatusBlock } from '@/components/elements/StatusBlock';

import { SecurityRow, UserAccountRow } from '../UserAccountsSecurity.types';

import AccountActionsMenu from './AccountActionsMenu';
import MfaLockStatus from './MfaLockStatus';

const HeaderBox = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.colors.border2};
  border-left: 1px solid ${({ theme }) => theme.colors.border2};
  border-right: 1px solid ${({ theme }) => theme.colors.border2};
  border-radius: ${({ theme }) => `${theme.rounds.container1Round} ${theme.rounds.container1Round} 0 0`};
`;

const ListBox = styled(Box)`
  border-radius: ${({ theme }) => theme.rounds.container1Round};
  background-color: ${({ theme }) => theme.colors.module};
`;

const ListBoxWrapper = styled(Box)`
  border-radius: ${({ theme }) => `0 0 ${theme.rounds.container1Round} ${theme.rounds.container1Round}`};
  border: 1px solid ${({ theme }) => theme.colors.border2};
  background: ${({ theme }) => theme.colors.canvas};
`;

const AccountsTableBox = styled(Box)`
  border: 1px solid ${({ theme }) => theme.colors.border2};
`;

const SecurityRowBox = styled(Box)`
  border: 1px solid ${({ theme }) => theme.colors.border2};
`;

const StyledTableRow = styled(Box)<{ $isLast?: boolean }>`
  ${({ $isLast, theme }) => !$isLast && `border-bottom: 1px solid ${theme.colors.border2};`}
`;

const ACCOUNT_COLUMNS = [
  { key: 'name', title: 'Name', flex: '1' as const },
  { key: 'employer', title: 'Employer', flex: '1' as const },
  { key: 'primary-account', title: 'Primary account', flex: '1' as const },
];

const SECURITY_COLUMNS = [
  { key: 'username', title: 'Username', flex: '0.9' as const },
  { key: 'password', title: 'Password', flex: '1.25' as const },
  { key: 'userAccess', title: 'User access', flex: '1' as const },
  { key: 'lockedCredentials', title: 'Locked credentials', flex: '1' as const },
  { key: 'mfa', title: 'MFA', flex: '1.2' as const },
];
const ACTIONS_COLUMN_FLEX = '0 0 40px';
const EMPTY_DASH = '–';

const getUserAccessStatus = (roleType?: Roles) => {
  if (roleType === Roles.employee) {
    return <StatusBlock testId="user-accounts-security_user-access" title="Full access" backgroundColor="success" />;
  }
  if (roleType === Roles.limitedAccess) {
    return <StatusBlock testId="user-accounts-security_user-access" title="Limited access" backgroundColor="warning" />;
  }
  return <StatusBlock testId="user-accounts-security_user-access" title="No access" backgroundColor="danger" />;
};

const getTableHeader = (
  columns: typeof ACCOUNT_COLUMNS | typeof SECURITY_COLUMNS,
  testIdPrefix: string,
  hasActionsColumn?: boolean,
  pad: { vertical: string; horizontal: string } = { vertical: 'spacing8', horizontal: 'spacing16' },
  height?: string,
) => (
  <Box
    direction="row"
    align="center"
    background="listHeaderBackgroundColor"
    round="container2Round"
    pad={pad}
    height={height}
  >
    {columns.map((column) => (
      <Box key={column.key} style={{ flex: column.flex }} pad={{ right: 'spacing8' }} data-testId={`${testIdPrefix}_${column.key}`}>
        <Text size="12px" weight="bold" color="textBody">{column.title}</Text>
      </Box>
    ))}
    {hasActionsColumn ? <Box style={{ flex: ACTIONS_COLUMN_FLEX }} /> : null}
  </Box>
);

interface Props {
  isLoading?: boolean;
  accounts: UserAccountRow[];
  securityRows: SecurityRow[];
  isEditMode?: boolean;
  hasLinkAccountAccess?: boolean;
  onLinkAccount?: () => void;
  onShowDetails?: () => void;
  onProvideFullAccess: (row: SecurityRow) => void;
  onLock: (row: SecurityRow) => void;
  onUnlock: (row: SecurityRow) => void;
  onUnlockMfa: (row: SecurityRow) => void;
  onRemoveCredentials: (row: SecurityRow) => void;
  linkedAccountsSlot?: React.ReactNode;
}

const UserAccountsSecurity: React.FC<Props> = ({
  isLoading,
  accounts,
  securityRows,
  isEditMode,
  hasLinkAccountAccess,
  onLinkAccount,
  onShowDetails,
  onProvideFullAccess,
  onLock,
  onUnlock,
  onUnlockMfa,
  onRemoveCredentials,
  linkedAccountsSlot,
}) => {
  return (
    <Box background="canvas" round="container1Round" data-testId="user-accounts-security">
      {linkedAccountsSlot}

      <HeaderBox
        background="canvas"
        direction="row"
        align="center"
        justify="between"
        pad={{ vertical: 'spacing24', horizontal: 'spacing32' }}
      >
        <Inscription size="xxlarge" lineHeight="28px" color="textTitle" data-testId="user-accounts-security_title">
          User Accounts
        </Inscription>

        {hasLinkAccountAccess ? (
          <Box direction="row" align="center">
            <AppButton
              testId="user-accounts-security_show-details"
              buttonType="secondary"
              width="150px"
              onClick={onShowDetails}
            >
              Show Details
            </AppButton>
            <Box margin={{ left: 'spacing12' }}>
              <AppButton
                testId="user-accounts-security_link-account"
                buttonType="primary"
                width="150px"
                onClick={onLinkAccount}
                disabled={!isEditMode}
              >
                Link Account
              </AppButton>
            </Box>
          </Box>
        ) : null}
      </HeaderBox>

      {isLoading ? (
        <Box pad={{ top: 'spacing16' }}>
          <Preloader testId="user-accounts-security_preloader" />
        </Box>
      ) : (
        <ListBoxWrapper pad="spacing12">
        <ListBox
          pad="spacing12"
          data-testId="user-accounts-security_list"
        >
          {getTableHeader(ACCOUNT_COLUMNS, 'user-accounts-security_header', false, { vertical: 'spacing2', horizontal: 'spacing24' }, '32px')}

          <AccountsTableBox background="canvas" round="container2Round" margin={{ top: 'spacing8' }}>
            <Box pad={{ horizontal: 'spacing12', bottom: 'spacing12' }}>
            <Box pad={{ vertical: 'spacing4' }}>
            {accounts.map((row, index) => (
              <StyledTableRow
                key={row.employeeId}
                $isLast={index === accounts.length - 1}
                direction="row"
                align="center"
                height={{ min: '40px' }}
                pad={{ vertical: 'spacing2', horizontal: 'spacing12' }}
                data-testId={`user-accounts-security_account_${row.employeeId}`}
              >
                <Box style={{ flex: ACCOUNT_COLUMNS[0].flex }} align="start" data-testId={`user-accounts-security_account_${row.employeeId}_name`}>
                  <RawButton onClick={() => window.open(ROUTES.EMPLOYEE_VIEW(row.employeeId), '_blank')}>
                    <Text size="medium" weight="bold">{row.fullName || EMPTY_FIELD_VALUE}</Text>
                  </RawButton>
                </Box>
                <Box style={{ flex: ACCOUNT_COLUMNS[1].flex }} data-testId={`user-accounts-security_account_${row.employeeId}_employer`}>
                  <Text size="medium">{row.employerName || EMPTY_FIELD_VALUE}</Text>
                </Box>
                <Box style={{ flex: ACCOUNT_COLUMNS[2].flex }} data-testId={`user-accounts-security_account_${row.employeeId}_primary-account`}>
                  <Text size="medium">{row.isPrimaryLinkedAccount ? 'Yes' : 'No'}</Text>
                </Box>
              </StyledTableRow>
            ))}
            </Box>

            <Box margin={{ top: 'spacing4' }}>
              <Box
                pad={{ vertical: 'spacing8', horizontal: 'spacing16' }}
                data-testId="user-accounts-security_security-title"
              >
                <Inscription size="large">
                  Security
                </Inscription>
              </Box>

              <Box background="module" round="container1Round" pad="spacing4">
              {getTableHeader(SECURITY_COLUMNS, 'user-accounts-security_header', true, { vertical: 'spacing2', horizontal: 'spacing12' }, '36px')}

              {securityRows.map((row) => (
                <SecurityRowBox
                  key={row.key}
                  background="canvas"
                  round="container2Round"
                  margin={{ top: 'spacing4' }}
                  height={{ min: '48px' }}
                  direction="row"
                  align="center"
                  pad={{ vertical: 'spacing2', horizontal: 'spacing12' }}
                  data-testId={`user-accounts-security_item_${row.key}`}
                >
              <Box style={{ flex: SECURITY_COLUMNS[0].flex }} direction="row" align="center" data-testId={`user-accounts-security_item_${row.key}_username`}>
                <Text size="medium" weight="bold">
                  {row.username || EMPTY_DASH}
                </Text>
              </Box>
              <Box style={{ flex: SECURITY_COLUMNS[1].flex }} data-testId={`user-accounts-security_item_${row.key}_password`}>
                <Text size="medium">
                  {row.lastPasswordUpdate
                    ? `Updated ${dayjs(row.lastPasswordUpdate).format(DEFAULT_DATE_FORMAT)}`
                    : EMPTY_DASH}
                </Text>
              </Box>
              <Box style={{ flex: SECURITY_COLUMNS[2].flex }} align="start" data-testId={`user-accounts-security_item_${row.key}_user-access`}>
                {getUserAccessStatus(row.roleType)}
              </Box>
              <Box style={{ flex: SECURITY_COLUMNS[3].flex }} data-testId={`user-accounts-security_item_${row.key}_locked-credentials`}>
                <Text size="medium" color={row.isLoginLocked ? 'danger' : undefined}>{row.isLoginLocked ? 'Yes' : 'No'}</Text>
              </Box>
              <Box style={{ flex: SECURITY_COLUMNS[4].flex }} align="start" data-testId={`user-accounts-security_item_${row.key}_mfa`}>
                <MfaLockStatus
                  rowKey={row.key}
                  hasMfa={row.hasMfa}
                  isMfaLocked={row.isMfaLocked}
                  mfaLockedTo={row.mfaLockedTo}
                />
              </Box>
              <Box style={{ flex: ACTIONS_COLUMN_FLEX }} align="end">
                {row.username ? (
                  <AccountActionsMenu
                    rowKey={row.key}
                    roleType={row.roleType}
                    isLoginLocked={row.isLoginLocked}
                    isMfaLocked={row.isMfaLocked}
                    canManageEmployeeAccess={Boolean(row.employeeId)}
                    onProvideFullAccess={() => onProvideFullAccess(row)}
                    onLock={() => onLock(row)}
                    onUnlock={() => onUnlock(row)}
                    onUnlockMfa={() => onUnlockMfa(row)}
                    onRemoveCredentials={() => onRemoveCredentials(row)}
                  />
                ) : null}
              </Box>
                </SecurityRowBox>
              ))}
              </Box>
            </Box>
            </Box>
          </AccountsTableBox>
        </ListBox>
        </ListBoxWrapper>
      )}
    </Box>
  );
};

export default UserAccountsSecurity;
